// MCHD Room Scheduler - Session Time Statistics Component
// Displays overview statistics for room session times

import React from 'react';
import {
  SimpleGrid,
  Paper,
  Stack,
  Group,
  Text,
  Progress,
  ThemeIcon,
  Alert,
  Center,
  Loader
} from '@mantine/core';
import {
  IconClock,
  IconCheck,
  IconX,
  IconBuilding,
  IconDoor,
  IconTrendingUp,
  IconAlertCircle
} from '@tabler/icons-react';

// Import types
import type { RoomSessionTimeStats } from '../../api/endpoints/session-times';

// =============================================================================
// INTERFACES
// =============================================================================

interface SessionTimeStatsProps {
  stats?: RoomSessionTimeStats | undefined;
  isLoading?: boolean;
  error?: any;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Session Time Statistics Component
 * Displays key metrics and statistics for room session times
 */
export function SessionTimeStats({ stats, isLoading, error }: SessionTimeStatsProps) {
  // =============================================================================
  // LOADING STATE
  // =============================================================================

  if (isLoading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="md">
          <Loader size="md" />
          <Text c="dimmed">Loading statistics...</Text>
        </Stack>
      </Center>
    );
  }

  // =============================================================================
  // ERROR STATE
  // =============================================================================

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Failed to Load Statistics"
        color="red"
        variant="light"
      >
        {error?.message || 'An error occurred while loading session time statistics'}
      </Alert>
    );
  }

  // =============================================================================
  // NO DATA STATE
  // =============================================================================

  if (!stats) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="No Data Available"
        color="yellow"
        variant="light"
      >
        No session time statistics are available at this time.
      </Alert>
    );
  }

  // =============================================================================
  // CALCULATIONS
  // =============================================================================

  const {
    totalSessionTimes,
    activeSessionTimes,
    inactiveSessionTimes,
    sessionsByRoom,
    sessionsByBuilding
  } = stats;

  // Calculate rates
  const activeRate = totalSessionTimes > 0 ? (activeSessionTimes / totalSessionTimes) * 100 : 0;
  const inactiveRate = totalSessionTimes > 0 ? (inactiveSessionTimes / totalSessionTimes) * 100 : 0;

  // Get top rooms by session count (with duplicate ID detection)
  const topRooms = (() => {
    const rooms = sessionsByRoom || [];
    const seenIds = new Set<string>();
    
    return rooms
      .filter(room => {
        // Filter out duplicates
        if (seenIds.has(room.roomId)) {
          console.warn('SessionTimeStats - Duplicate room ID detected:', room.roomId);
          return false;
        }
        seenIds.add(room.roomId);
        return true;
      })
      .sort((a, b) => b.sessionCount - a.sessionCount)
      .slice(0, 3);
  })();

  // Get top buildings by session count (with duplicate ID detection)
  const topBuildings = (() => {
    const buildings = sessionsByBuilding || [];
    const seenIds = new Set<string>();
    
    return buildings
      .filter(building => {
        // Filter out duplicates
        if (seenIds.has(building.buildingId)) {
          console.warn('SessionTimeStats - Duplicate building ID detected:', building.buildingId);
          return false;
        }
        seenIds.add(building.buildingId);
        return true;
      })
      .sort((a, b) => b.sessionCount - a.sessionCount)
      .slice(0, 3);
  })();

  // =============================================================================
  // STAT CARD COMPONENT
  // =============================================================================

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    description, 
    trend 
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    description?: string;
    trend?: { value: number; label: string; color: string };
  }) => (
    <Paper p="md" radius="md" withBorder>
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text size="sm" c="dimmed" fw={500}>
              {title}
            </Text>
            <Text size="xl" fw={700} c={color}>
              {value}
            </Text>
            {description && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </div>
          <ThemeIcon size="lg" color={color} variant="light">
            {icon}
          </ThemeIcon>
        </Group>
        
        {trend && (
          <Group gap="xs">
            <Text size="xs" c={trend.color} fw={500}>
              {trend.value > 0 ? '+' : ''}{trend.value}%
            </Text>
            <Text size="xs" c="dimmed">
              {trend.label}
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Stack gap="lg">
      {/* Main Statistics Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <StatCard
          title="Total Session Times"
          value={totalSessionTimes.toLocaleString()}
          icon={<IconClock size={20} />}
          color="blue"
          description="All session times"
        />
        
        <StatCard
          title="Active Sessions"
          value={activeSessionTimes.toLocaleString()}
          icon={<IconCheck size={20} />}
          color="green"
          description="Currently available"
        />
        
        <StatCard
          title="Inactive Sessions"
          value={inactiveSessionTimes.toLocaleString()}
          icon={<IconX size={20} />}
          color="red"
          description="Temporarily disabled"
        />
        
        <StatCard
          title="Active Rate"
          value={`${activeRate.toFixed(1)}%`}
          icon={<IconTrendingUp size={20} />}
          color="teal"
          description="Percentage active"
        />
      </SimpleGrid>

      {/* Progress Bars */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Paper p="md" radius="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={600} size="sm">
                Session Status Distribution
              </Text>
              <Text size="xs" c="dimmed">
                {totalSessionTimes} total
              </Text>
            </Group>
            
            <Stack gap="xs">
              <div>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" c="green">
                    Active Sessions
                  </Text>
                  <Text size="xs" c="dimmed">
                    {activeSessionTimes}
                  </Text>
                </Group>
                <Progress
                  value={activeRate}
                  color="green"
                  size="sm"
                  radius="xs"
                />
              </div>
              
              <div>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" c="red">
                    Inactive Sessions
                  </Text>
                  <Text size="xs" c="dimmed">
                    {inactiveSessionTimes}
                  </Text>
                </Group>
                <Progress
                  value={inactiveRate}
                  color="red"
                  size="sm"
                  radius="xs"
                />
              </div>
            </Stack>
          </Stack>
        </Paper>

        <Paper p="md" radius="md" withBorder>
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Session Distribution
            </Text>
            
            <Stack gap="xs">
              <div>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" c="blue">
                    By Room
                  </Text>
                  <Text size="xs" c="dimmed">
                    {(sessionsByRoom || []).length} rooms
                  </Text>
                </Group>
                <Progress
                  value={75}
                  color="blue"
                  size="sm"
                  radius="xs"
                />
              </div>
              
              <div>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" c="orange">
                    By Building
                  </Text>
                  <Text size="xs" c="dimmed">
                    {(sessionsByBuilding || []).length} buildings
                  </Text>
                </Group>
                <Progress
                  value={60}
                  color="orange"
                  size="sm"
                  radius="xs"
                />
              </div>
            </Stack>
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Top Rooms and Buildings */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {/* Top Rooms */}
        <Paper p="md" radius="md" withBorder>
          <Stack gap="sm">
            <Group>
              <ThemeIcon size="md" color="blue" variant="light">
                <IconDoor size={16} />
              </ThemeIcon>
              <Text fw={600} size="sm">
                Top Rooms by Sessions
              </Text>
            </Group>
            
            {topRooms.length > 0 ? (
              <Stack gap="xs">
                {topRooms.map((room) => (
                  <Group key={room.roomId} justify="space-between">
                    <div>
                      <Text size="sm" fw={500}>
                        {room.roomName} ({room.roomNumber})
                      </Text>
                    </div>
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        {room.sessionCount}
                      </Text>
                      <ThemeIcon size="xs" color="blue" variant="light">
                        <IconClock size={12} />
                      </ThemeIcon>
                    </Group>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No room data available
              </Text>
            )}
          </Stack>
        </Paper>

        {/* Top Buildings */}
        <Paper p="md" radius="md" withBorder>
          <Stack gap="sm">
            <Group>
              <ThemeIcon size="md" color="orange" variant="light">
                <IconBuilding size={16} />
              </ThemeIcon>
              <Text fw={600} size="sm">
                Top Buildings by Sessions
              </Text>
            </Group>
            
            {topBuildings.length > 0 ? (
              <Stack gap="xs">
                {topBuildings.map((building) => (
                  <Group key={building.buildingId} justify="space-between">
                    <div>
                      <Text size="sm" fw={500}>
                        {building.buildingName}
                      </Text>
                    </div>
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        {building.sessionCount}
                      </Text>
                      <ThemeIcon size="xs" color="orange" variant="light">
                        <IconClock size={12} />
                      </ThemeIcon>
                    </Group>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No building data available
              </Text>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}
